import { SearchParameters } from "@coveo/headless";
import { CoveoHeadlessTab } from "../types/coveo-headless-types";

/**
 * Using the url hash, create a parameter object that will be used
 * by coveo's searchParameterManager to filter results
 */
const deserializeUrl = (
	aq: string,
	defaultSortCriteria: string,
	sortByDictionary: string[],
	tabs: CoveoHeadlessTab,
	urlHash: string
) => {
	const newParameters: SearchParameters = {};
	if (urlHash) {
		const hash = decodeURIComponent(urlHash);
		// convert urlHash string to an object
		// {"t": "catPerspectives", "sort": "@publishdate descending","f:@tagtopic": "[Fixed Income Investing]"}
		const hashToObj: { [key: string]: string } = hash
			.split("&")
			.map((v) => v.split("="))
			.reduce((pre, [key, value]) => ({ ...pre, [key]: value }), {});

		// sort by criterion builder
		newParameters.sortCriteria = defaultSortCriteria;
		if (hashToObj["sort"]) {
			const hashSortBy = hashToObj["sort"];
			// for pages with a Sort By dropdown, make sure that value is available in dictionary
			// otherwise set to default value
			if (sortByDictionary.find((sortByVal) => sortByVal === hashSortBy)) {
				newParameters.sortCriteria = hashSortBy;
			}
		}

		// facets builder
		const urlFacets: Record<string, string[]> = {};
		Object.keys(hashToObj).forEach((facet) => {
			const facetMatch = facet.match(/f:@(.*?)$/);
			if (facetMatch) {
				const facetName = facetMatch[1];
				if (["taginsightsauthor", "tagauthorcitation"].includes(facetName)) {
					// any author with a PhD will be labeled as "FirstName LastName, PhD"
					// first split the array by commas, then add the comma back to any with PhDs
					// correct facet list: ([Marlena Lee, PhD], [David Butler])
					// otherwise on screen facets will be ([Marlena Lee], [PhD], [David Butler])
					const hashFacet = hashToObj[`f:@${facetName}`];
					const tempParamAuthor = decodeURI(hashFacet)
						.replace(/,\s+(?=PhD)/g, " ")
						.replace(/,\s/g, " ")
						.replace(/\[|\]/g, "")
						.split(",");
					const paramTagAuthor = tempParamAuthor.map((item: string) =>
						item.replace(/\s+(?=PhD)/, ", ").trim()
					);
					urlFacets[facetName] = [...paramTagAuthor];
				} else {
					const facetValues = hashToObj[`f:@${facetName}`]
						.replace(/\[|\]/g, "")
						.split(",")
						.map((topic: string) => topic.trim());
					urlFacets[facetName] = [...facetValues];
				}
			}
		});
		newParameters.f = urlFacets;

		// tab builder
		if (hashToObj["t"]) {
			if (Object.keys(tabs).length) {
				// ["All","Perspectives","Research","Webcasts"]
				const tabKeys = Object.keys(tabs);
				const newTabs = { ...tabs };
				for (const key of tabKeys) {
					const hashTab = hashToObj.t;
					// tab filter in url begins with 'cat', get the substring containing just the tab name
					// i.e. catPerspectives, catResearch
					if (key === hashTab.substring(hashTab.indexOf("cat") + 3)) {
						newParameters.aq = newTabs[key].tabFacet + aq;
						newParameters.tab = key;
					}
				}
			}
		}
		// for pages with no tabs, add the aq when url is changed manually or
		// for situations when there is a # option that is not recognized (https://www.dimensional.com/us-en/insights#Dimensional%20Insights)
		else {
			newParameters.aq = aq;
		}
		// search page query builder
		if (hashToObj["q"]) {
			newParameters.q = hashToObj["q"];
		}
	}
	return newParameters;
};

export default deserializeUrl;
